import { LogOut, Sun, Moon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuthStore } from '@/stores/authStore';
import { FOOTER_NAV_IDS, FOOTER_NAV_ORDER, type NavItem } from '@/types/navigation';
import { StatusBar } from './StatusBar';

interface AuthShellProps<V extends string> {
  navItems: NavItem<V>[];
  children: React.ReactNode;
  brandLetter?: string;
}

export function AuthShell<V extends string>({
  navItems,
  children,
  brandLetter = 'K',
}: AuthShellProps<V>) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const currentView = useLocation().pathname.split('/')[1] || '';

  let lastSection: string | undefined;
  const mainNavItems = navItems.filter((item) => !FOOTER_NAV_IDS.has(item.id));
  const footerNavItems = navItems
    .filter((item) => FOOTER_NAV_IDS.has(item.id))
    .sort((a, b) => FOOTER_NAV_ORDER.indexOf(a.id) - FOOTER_NAV_ORDER.indexOf(b.id));

  return (
    <div data-view={currentView} className="bg-background text-foreground flex h-screen overflow-hidden">
      <aside className="bg-muted border-border/50 w-16 border-r">
        <div className="flex h-full flex-col">
          {/* Brand */}
          <div className="border-border flex h-12 items-center justify-center border-b">
            <span className="text-primary text-lg font-bold">{brandLetter}</span>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-2">
            {mainNavItems.map((item) => {
              const showDivider = lastSection !== undefined && item.section !== lastSection;
              lastSection = item.section;
              return (
                <div key={item.id}>
                  {showDivider && <div className="border-border mx-3 my-1 border-t" />}
                  <NavLinkButton item={item} />
                </div>
              );
            })}
          </nav>

          {/* Footer: user, settings, theme, logout */}
          <div className="border-border space-y-1 border-t py-2">
            {user && (
              <div className="flex h-8 items-center justify-center">
                <span className="text-muted-foreground truncate text-[10px]">{user.username}</span>
              </div>
            )}
            {footerNavItems.map((item) => (
              <NavLinkButton key={item.id} item={item} />
            ))}
            <ThemeButton />
            <button
              onClick={() => logout()}
              title="Sign out"
              className={cn(
                'group relative flex h-12 w-full items-center justify-center transition-colors',
                'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
              )}
            >
              <LogOut className="h-5 w-5" />
              <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
                Sign out
              </div>
            </button>
          </div>
        </div>
      </aside>
      <main className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-hidden">{children}</div>
        <StatusBar />
      </main>
    </div>
  );
}

// Single sidebar nav button. Active state is derived from the URL (not props) so
// browser back/forward, deep links, and "open in new tab" all just work.
// A section with `tabs` renders the icon (→ its default page) plus a hover flyout
// listing the sub-pages; each is a direct link, so you can jump straight to any tab.
function NavLinkButton<V extends string>({ item }: { item: NavItem<V> }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  const pathActive = (p: string) => pathname === p || pathname.startsWith(p + '/');
  const isActive = pathActive(item.path) || !!item.tabs?.some((t) => pathActive(t.path));

  const iconInner = (
    <>
      <Icon className="h-5 w-5" />
      {item.badge && (
        <span className="bg-muted-foreground/20 text-muted-foreground absolute bottom-1 right-1 rounded px-0.5 text-[7px] font-semibold uppercase leading-tight">
          {item.badge}
        </span>
      )}
      {isActive && (
        <div className="bg-primary absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r" />
      )}
    </>
  );
  const iconClasses = cn(
    'relative flex h-12 w-full items-center justify-center transition-colors',
    !isActive && 'text-muted-foreground group-hover:text-foreground',
    isActive && 'text-primary bg-primary/10',
  );

  // Grouped section: icon + hover flyout of tabs (links are siblings, never nested).
  if (item.tabs?.length) {
    return (
      <div className="group relative">
        <Link to={item.path} data-nav-button={item.id} title={item.label} className={cn(iconClasses, !isActive && 'group-hover:bg-muted/50')}>
          {iconInner}
        </Link>
        <div className="invisible absolute left-full top-0 z-50 pl-2 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
          <div className="border-border bg-popover min-w-[172px] rounded-md border py-1 shadow-lg">
            <div className="text-muted-foreground px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide">
              {item.label}
            </div>
            {item.tabs.map((t) => {
              const tabActive = pathActive(t.path);
              return (
                <Link
                  key={t.id}
                  to={t.path}
                  className={cn(
                    'flex items-center justify-between gap-3 px-3 py-1.5 text-xs',
                    tabActive ? 'text-primary bg-primary/10' : 'text-foreground/80 hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <span>{t.label}</span>
                  {t.badge && (
                    <span className="bg-muted-foreground/20 text-muted-foreground rounded px-1 text-[8px] font-semibold uppercase">
                      {t.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Plain section: icon + a simple label tooltip.
  return (
    <Link to={item.path} data-nav-button={item.id} title={item.label} className={cn('group', iconClasses, !isActive && 'hover:bg-muted/50', 'hover:text-foreground')}>
      {iconInner}
      <div className="bg-popover text-popover-foreground invisible absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {item.label}
      </div>
    </Link>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50',
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {isDark ? 'Light mode' : 'Dark mode'}
      </div>
    </button>
  );
}
